import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Loader2, AlertTriangle } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import api from '../api';
import { INTERVENTION_STATUS, INTERVENTION_TYPES, URGENCY_LEVELS, formatDate } from '../constants';

const AdminInterventions = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (status && status !== 'all') params.append('status', status);
      const { data } = await api.get(`/interventions?${params.toString()}`);
      setItems(data);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div data-testid="admin-interventions">
      <h1 className="text-2xl font-bold text-neutral-900 mb-1">Demandes d'intervention</h1>
      <p className="text-neutral-500 mb-6">{items.length} demande(s)</p>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input placeholder="Rechercher par n°, client, ville..." value={search}
            onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-white" data-testid="intervention-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-56 bg-white" data-testid="intervention-status-filter"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(INTERVENTION_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-red-700" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-neutral-200 p-12 text-center text-neutral-400">Aucune demande trouvée.</div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const st = INTERVENTION_STATUS[it.status] || {};
            const ur = URGENCY_LEVELS[it.urgency] || {};
            const urgent = ['haute', 'critique'].includes(it.urgency);
            return (
              <Link key={it.id} to={`/admin/interventions/${it.id}`} data-testid={`intervention-row-${it.number}`}
                className={`block bg-white rounded-xl border p-4 hover:shadow-sm transition-shadow ${urgent ? 'border-l-4 border-l-red-500 border-neutral-200' : 'border-neutral-200'}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-bold text-neutral-900">{it.number}</span>
                      {urgent && <AlertTriangle className="w-4 h-4 text-red-500" />}
                      <span className={`text-xs px-2 py-0.5 rounded ${ur.color}`}>{ur.label}</span>
                      <span className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded">{INTERVENTION_TYPES[it.type]}</span>
                    </div>
                    <p className="font-medium text-neutral-800">{it.clientName}</p>
                    <p className="text-sm text-neutral-500 truncate">{it.city || ''} · {formatDate(it.createdAt)}</p>
                    <p className="text-sm text-neutral-500 truncate mt-0.5">{it.description}</p>
                  </div>
                  <div className="shrink-0">
                    <span className={`text-xs px-2.5 py-1 rounded ${st.color}`}>{st.label}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminInterventions;
